import { Link } from 'react-router-dom';
import { CLIMATIZACAO_LABELS, PAPEL_LABELS } from '@adega/shared';
import { ErrorBox, Loading } from '../components/Feedback.js';
import { PageHeader } from '../components/PageHeader.js';
import { TacaVazia } from '../components/TacaVazia.js';
import { useAuth, useBottles, useCellars, useWaiting } from '../api/queries.js';
import { inicial, primeiroNome } from '../lib/nome.js';

/**
 * Tela inicial: as adegas da conta.
 *
 * Mesmo com uma adega só, o início é a lista — é o ponto de partida estável da
 * navegação ("minhas adegas" → prateleiras → garrafa), e é aqui que se cadastra
 * a próxima adega.
 */
export function CellarList() {
  const { data: auth } = useAuth();
  const { data: cellars, isLoading, error } = useCellars();

  if (isLoading) return <Loading texto="Carregando suas adegas…" />;
  if (error) return <ErrorBox erro={error} />;

  const totalGarrafas = cellars?.reduce((soma, c) => soma + c.bottleCount, 0) ?? 0;
  // A mesma consulta que a tela "Já bebi" usa: pedir aqui deixa a lista pronta
  // no cache antes de a pessoa tocar no cartão.
  const { data: bebidas } = useBottles({ status: 'consumed' });
  const jaBebidas = bebidas?.length ?? 0;
  const { data: fila } = useWaiting();
  const naFila = fila?.length ?? 0;
  const nome = primeiroNome(auth?.user?.name);

  /*
   * Primeira vez: nenhuma adega e nenhuma garrafa.
   *
   * Esta tela pergunta o que importa -- "onde você guarda seus vinhos?" -- em
   * vez de assumir que existe um móvel. Até 2026-10-02 o app exigia configurar
   * uma adega antes de qualquer coisa, e isso trancava do lado de fora quem tem
   * oito garrafas num armário: a pessoa precisava inventar prateleiras e
   * capacidade para um lugar que não tem nenhuma das duas.
   *
   * Com garrafas mas sem adega não é primeira vez: ali a tela já tem o que
   * mostrar, e o cartão das garrafas sem adega faz o papel.
   */
  const semAdegas = (cellars?.length ?? 0) === 0;
  const primeiraVez = semAdegas && totalGarrafas === 0 && naFila === 0;

  if (primeiraVez) {
    return (
      <div className="space-y-4">
        <PageHeader
          titulo={nome ? `Olá, ${nome}` : 'Minha Adega'}
          subtitulo="Vamos começar pelo lugar onde seus vinhos estão hoje"
        />

        <p className="text-sm text-adega-muted">
          Este app guarda o que você tem e diz quais garrafas abrir antes de passarem do ponto.
          Para começar, me diga onde elas ficam.
        </p>

        <Link
          to="/nova-adega"
          className="card flex items-center gap-4 transition hover:border-adega-wine-light active:scale-[0.99]"
        >
          <span aria-hidden className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-adega-wine/25 text-2xl">
            🍷
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-lg font-semibold">Tenho uma adega</p>
            <p className="text-sm text-adega-muted">
              Um móvel ou geladeira de vinhos, com prateleiras. O app guarda em que posição cada
              garrafa está.
            </p>
          </div>
          <span aria-hidden className="text-adega-muted">›</span>
        </Link>

        {/* O caminho que não existia. Vai direto cadastrar, sem móvel nenhum --
            a garrafa nasce sem adega, que o banco e o domínio já aceitam. */}
        <Link
          to="/adicionar"
          className="card flex items-center gap-4 transition hover:border-adega-wine-light active:scale-[0.99]"
        >
          <span aria-hidden className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-adega-surface-2 text-2xl">
            📦
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-lg font-semibold">Estão guardados por aí</p>
            <p className="text-sm text-adega-muted">
              Num armário, numa caixa, numa prateleira qualquer. Cadastre as garrafas agora e
              organize depois, se quiser.
            </p>
          </div>
          <span aria-hidden className="text-adega-muted">›</span>
        </Link>

        <p className="pt-2 text-center text-xs text-adega-muted">
          Comece com 3 garrafas — leva dois minutos. Dá para cadastrar o resto aos poucos.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <PageHeader
        titulo={semAdegas ? 'Minhas garrafas' : 'Minhas adegas'}
        /*
          "Guardadas" conta só o que está dentro de uma adega, e isso é decisão
          deliberada (2026-09-21): guardada é estar no móvel; quem espera vaga
          está no armário. Mas para quem não tem móvel nenhum, "0 garrafas
          guardadas" logo acima de um cartão dizendo "3 garrafas" é o app se
          contradizendo na mesma tela.
          
          Palavra diferente para coisa diferente: sem adega, o número que
          importa é quantas foram CADASTRADAS, que é o que a pessoa tem.
        */
        subtitulo={`${nome ? `Olá, ${nome} · ` : ''}${
          semAdegas
            ? `${naFila} ${naFila === 1 ? 'garrafa cadastrada' : 'garrafas cadastradas'}`
            : `${totalGarrafas} ${totalGarrafas === 1 ? 'garrafa guardada' : 'garrafas guardadas'}`
        }`}
        acao={
          // Perfil pela inicial, no canto: "Sair" e trocar senha ficam lá dentro.
          <Link
            to="/perfil"
            aria-label="Perfil"
            className="grid h-10 w-10 place-items-center rounded-full bg-adega-wine text-base font-semibold text-white ring-2 ring-adega-wine-light/40 transition active:scale-95"
          >
            {inicial(auth?.user?.name)}
          </Link>
        }
      />

      <ul className="space-y-3">
        {cellars?.map((cellar) => {
          const ocupacao = cellar.capacity > 0 ? cellar.occupied / cellar.capacity : 0;

          return (
            <li key={cellar.id}>
              <Link
                to={`/adega/${cellar.id}`}
                className="card flex items-center gap-4 transition hover:border-adega-wine-light active:scale-[0.99]"
              >
                <span
                  aria-hidden
                  className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-adega-wine/25 text-2xl"
                >
                  🍷
                </span>

                <div className="min-w-0 flex-1">
                  <p className="truncate text-lg font-semibold">{cellar.name}</p>
                  <p className="text-sm text-adega-muted">
                    {cellar.occupied} de {cellar.capacity} posições · {cellar.shelfCount}{' '}
                    {cellar.shelfCount === 1 ? 'prateleira' : 'prateleiras'}
                  </p>
                  {cellar.bottleCount > cellar.occupied ? (
                    <p className="text-xs text-adega-gold">
                      {cellar.bottleCount - cellar.occupied} sem posição definida
                    </p>
                  ) : null}
                  {cellar.climatizacao || cellar.papel ? (
                    <p className="text-xs text-adega-muted">
                      {[
                        cellar.climatizacao ? CLIMATIZACAO_LABELS[cellar.climatizacao] : null,
                        cellar.papel ? PAPEL_LABELS[cellar.papel] : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  ) : null}

                  <div
                    className="mt-2 h-1.5 overflow-hidden rounded-full bg-adega-surface-2"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={cellar.capacity}
                    aria-valuenow={cellar.occupied}
                    aria-label={`Ocupação de ${cellar.name}`}
                  >
                    <div
                      className="h-full rounded-full bg-adega-wine-light"
                      style={{ width: `${Math.round(ocupacao * 100)}%` }}
                    />
                  </div>
                </div>

                <span aria-hidden className="text-xl text-adega-muted">
                  ›
                </span>
              </Link>
            </li>
          );
        })}
      </ul>

      {/* Entrada pela tela inicial, e não por mais uma aba: a barra de baixo já
          tem quatro itens no celular. Depois das adegas, que são o principal. */}
      {totalGarrafas > 0 ? (
        <Link
          to="/numeros"
          className="card flex items-center gap-4 transition hover:border-adega-wine-light active:scale-[0.99]"
        >
          <span
            aria-hidden
            className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-adega-gold/15 text-2xl"
          >
            📊
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-lg font-semibold">Sua adega em números</p>
            <p className="text-sm text-adega-muted">Países, estilos, uvas e destaques</p>
          </div>
          <span aria-hidden className="text-xl text-adega-muted">
            ›
          </span>
        </Link>
      ) : null}

      {/* Conselho é coisa de sofá, e por isso mora aqui e não na barra de
          baixo — que é para o que se usa na frente da adega. Separado de
          "Números" porque contar o que existe e decidir o que fazer com isso
          são perguntas diferentes: uma é grátis, a outra custa uma chamada. */}
      {totalGarrafas > 0 ? (
        <Link
          to="/composicao"
          className="card flex items-center gap-4 transition hover:border-adega-wine-light active:scale-[0.99]"
        >
          <span
            aria-hidden
            className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-adega-gold/15 text-2xl"
          >
            🧭
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-lg font-semibold">Como está sua adega</p>
            <p className="text-sm text-adega-muted">Quando vence, o que falta, o que comprar</p>
          </div>
          <span aria-hidden className="text-xl text-adega-muted">
            ›
          </span>
        </Link>
      ) : null}

      {/* Só aparece quando há alguém esperando: adega em dia não mostra fila
          vazia. É o cartão que dá visibilidade à garrafa que não está em adega
          nenhuma — antes dele, ela não aparecia em lugar nenhum do app. */}
      {naFila > 0 ? (
        <Link
          to="/esperando-vaga"
          className="card flex items-center gap-4 transition hover:border-adega-wine-light active:scale-[0.99]"
        >
          <span
            aria-hidden
            className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-adega-gold/15 text-2xl"
          >
            📦
          </span>
          {/* Sem nenhuma adega cadastrada, estas garrafas não esperam vaga
              nenhuma: elas são simplesmente as garrafas da pessoa, guardadas
              num armário ou numa caixa. Chamar isso de "esperando vaga" seria
              inventar uma fila para quem nunca pediu uma. */}
          <div className="min-w-0 flex-1">
            <p className="text-lg font-semibold">{semAdegas ? 'Suas garrafas' : 'Esperando vaga'}</p>
            <p className="text-sm text-adega-muted">
              {naFila === 1 ? '1 garrafa' : `${naFila} garrafas`}
              {semAdegas
                ? naFila === 1
                  ? ' · guardada por aí'
                  : ' · guardadas por aí'
                : ' · por ordem de quem mais perde esperando'}
            </p>
          </div>
          <span aria-hidden className="text-xl text-adega-muted">
            ›
          </span>
        </Link>
      ) : null}

      {/* O histórico entra aqui, e não numa aba: a barra de baixo é para o que
          se usa na frente da adega. "Já bebi" é consulta, não ação do momento. */}
      {jaBebidas > 0 ? (
        <Link
          to="/ja-bebi"
          className="card flex items-center gap-4 transition hover:border-adega-wine-light active:scale-[0.99]"
        >
          {/* Taça vazia, e não o 🍷 das adegas: o mesmo ícone em dois cartões
              faria "Já bebi" parecer mais uma adega. */}
          <span
            aria-hidden
            className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-adega-wine/20 text-adega-wine-light"
          >
            <TacaVazia />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-lg font-semibold">Já bebi</p>
            <p className="text-sm text-adega-muted">
              {jaBebidas === 1 ? '1 garrafa' : `${jaBebidas} garrafas`} · com data, nota e comentário
            </p>
          </div>
          <span aria-hidden className="text-xl text-adega-muted">
            ›
          </span>
        </Link>
      ) : null}

      <Link
        to="/nova-adega"
        className="card flex items-center justify-center gap-2 border-dashed py-5 text-adega-muted transition hover:border-adega-wine-light hover:text-adega-text"
      >
        <span aria-hidden className="text-xl">
          ＋
        </span>
        {semAdegas ? 'Cadastrar uma adega' : 'Cadastrar outra adega'}
      </Link>
    </div>
  );
}
